import { useCallback, useMemo } from 'react';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Text } from 'foundations-components/components/Text';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Link } from 'foundations-components/transitional/components/Link';
import { PlanCompatibilityMessage } from '../../compatibility/PlanCompatibilityMessage';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { PermissionsMessage } from '../../permissions/PermissionsMessage';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ProductRequirementsCard({
  connectionType,
  features,
  builtByHubSpot,
  installability,
  missingCompatibilityRequirements,
  neededScopeGroups,
  onCompatibilityModalOpen,
  pricingPlans,
  tiers
}) {
  const {
    name,
    productType,
    published,
    appContext,
    trackInteraction
  } = useDetailsPageContext();
  const {
    hubInstallable
  } = installability || {};
  const scrollToPricing = () => {
    const tab = document.querySelector(`#details-page-pricing`);
    if (tab) {
      tab.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const compatibilityContext = appContext === 'app' ? 'app' : 'public';
  const isPreview = !published;
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product requirements card'
    }, eventProps));
  }, [trackInteraction]);
  const compatibilityType = useMemo(() => {
    const filteredRequirements = missingCompatibilityRequirements === null || missingCompatibilityRequirements === void 0 ? void 0 : missingCompatibilityRequirements.filter(requirement => !!requirement);
    const noCompatibleFeatures = (features === null || features === void 0 ? void 0 : features.length) && (filteredRequirements === null || filteredRequirements === void 0 ? void 0 : filteredRequirements.length) === (features === null || features === void 0 ? void 0 : features.length) && (filteredRequirements === null || filteredRequirements === void 0 ? void 0 : filteredRequirements.every(
    // USER_CONDITIONAL_SCOPE shows as COMPATIBLE_SOME
    requirement => requirement !== 'USER_CONDITIONAL_SCOPE'));
    if (!hubInstallable || noCompatibleFeatures) {
      return 'COMPATIBLE_NONE';
    } else if (filteredRequirements !== null && filteredRequirements !== void 0 && filteredRequirements.length) {
      return 'COMPATIBLE_SOME';
    } else {
      return 'COMPATIBLE_ALL';
    }
  }, [hubInstallable, missingCompatibilityRequirements, features]);
  const renderedPricingMessage = useMemo(() => {
    if (productType === 'APP' && pricingPlans) {
      return /*#__PURE__*/_jsx(Link, {
        onClick: () => {
          handleTrackInteraction({
            action: 'click pricing link'
          });
          scrollToPricing();
        },
        children: /*#__PURE__*/_jsx(Text, {
          color: "text-core-default",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: `detailsPage.requirementsCard.pricing.text.${productType}`,
            options: {
              count: pricingPlans.length,
              plans: /*#__PURE__*/_jsx(FormattedList, {
                exclusive: true,
                children: pricingPlans.map((pricing, index) => /*#__PURE__*/_jsx("span", {
                  children: pricing.pricingName
                }, `pricing-${index}`))
              })
            }
          })
        })
      });
    } else if (productType === 'AGENT') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.requirementsCard.pricing.text.${productType}`,
        options: {
          count: 2
        }
      });
    }
    return null;
  }, [handleTrackInteraction, pricingPlans, productType]);
  if (productType === 'AGENT') return null;
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.requirementsCard.title"
    }),
    children: /*#__PURE__*/_jsxs(DescriptionList, {
      children: [!isPreview && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.hubspotPlan"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(PlanCompatibilityMessage, {
            appContext: compatibilityContext,
            compatibilityType: compatibilityType,
            hasFeatureLevelScopeInfo: !!(features !== null && features !== void 0 && features.length),
            helpText: compatibilityType === 'COMPATIBLE_SOME' ? /*#__PURE__*/_jsx(HelpIcon, {
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.compatibility.disclaimer"
              })
            }) : undefined,
            onCompatibilityModalOpen: () => {
              handleTrackInteraction({
                action: 'open compatibility modal'
              });
              onCompatibilityModalOpen();
            },
            onTrackInteraction: handleTrackInteraction,
            productType: productType,
            tiers: tiers
          })
        })]
      }), neededScopeGroups && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.permissions.title"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(PermissionsMessage, {
            connectionType: connectionType,
            builtByHubSpot: builtByHubSpot,
            neededScopeGroups: neededScopeGroups,
            onTrackInteraction: handleTrackInteraction
          })
        })]
      }), pricingPlans && pricingPlans.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.requirementsCard.pricing.title.${productType}`,
            options: {
              name
            }
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: renderedPricingMessage
        })]
      })]
    })
  });
}